/**
 * The address as the delivery picker shows it: in the summary under "Deliver
 * to", and as a row in the address book modal. One shape for both books (the
 * customer spine and Tharstern's replica) so the summary and the rows read the
 * same whichever book an address came from.
 */

import type {
  CatalogueDeliveryAddress,
  CustomerAddress,
  DeliveryAddress,
} from "@shared-core/data/api-types";

export interface PickerAddress {
  /** The CIS address id on the spine book, Tharstern's address code on the replica book. */
  id: string;
  name: string;
  /** The address lines and the town, joined for one line. */
  address: string;
  postcode: string;
  /** Shown only when it is not the United Kingdom. */
  country: string;
  /** The customer's default delivery address. */
  isDefault: boolean;
  /** Added in One and not yet in Tharstern's address book. */
  isCisOnly: boolean;
}

/** One page of addresses from a book: the replica book is a single page. */
export interface PickerAddressPage {
  addresses: PickerAddress[];
  /** Whether a further page exists for the same search. */
  hasMore: boolean;
}

export function spineToPickerAddress(
  a: CatalogueDeliveryAddress,
): PickerAddress {
  return {
    id: a.id,
    name: a.name,
    address: [...a.lines, a.town].filter(Boolean).join(", "),
    postcode: a.postcode ?? "",
    country: a.country_code ?? "",
    isDefault: a.is_default_delivery,
    isCisOnly: !a.tharstern_customer_code,
  };
}

/** A just-captured spine address, as the book would list it. */
export function capturedToPickerAddress(c: CustomerAddress): PickerAddress {
  return {
    id: c.id,
    name: c.name,
    address: [c.line1, c.line2, c.line3, c.line4, c.town]
      .filter(Boolean)
      .join(", "),
    postcode: c.postcode ?? "",
    country: c.country_code ?? "",
    isDefault: c.is_default_delivery,
    isCisOnly: !c.tharstern_customer_code,
  };
}

export function replicaToPickerAddress(a: DeliveryAddress): PickerAddress {
  return {
    id: a.address_code,
    name: a.name || a.address_code,
    address: a.address,
    postcode: a.postcode,
    country: a.country,
    isDefault: a.is_default,
    isCisOnly: false,
  };
}

/** The addresses whose name, address or postcode contains the search, ignoring case. */
export function filterPickerAddresses(
  addresses: readonly PickerAddress[],
  query: string,
): PickerAddress[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...addresses];
  return addresses.filter((a) =>
    [a.name, a.address, a.postcode].some((field) =>
      field.toLowerCase().includes(needle),
    ),
  );
}

const UNITED_KINGDOM = new Set(["", "gb", "uk", "united kingdom"]);

/** The line under the address: the postcode, and the country when it is not the United Kingdom. */
export function postcodeLine(address: PickerAddress): string {
  const abroad = !UNITED_KINGDOM.has(address.country.trim().toLowerCase());
  return [address.postcode, abroad ? address.country : ""]
    .filter(Boolean)
    .join(", ");
}
